/* eslint-disable react-hooks/exhaustive-deps */
import * as Yup from "yup";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
// form
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
// @mui
import { LoadingButton } from "@mui/lab";
import Iconify from "src/components/iconify";

import {
  Box,
  Card,
  Grid,
  Stack,
  Alert,
  Paper,
  Button,
  Typography,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Chip,
} from "@mui/material";
// components
import Label from "src/components/label";
import { useSnackbar } from "../../components/snackbar";
import FormProvider, {
  RHFTextField,
} from "../../components/hook-form";
import { dispatch } from "src/redux/store";
import { routePrefix } from "src/utils/methods/StandardMethods";
import { PATH_DASHBOARD } from "src/routes/paths";
import useResponsive from "src/hooks/useResponsive";
import { fetchAllRoles } from "src/redux/slices/role";
import { useLocales } from "src/locales";
import { DATA_TRAINING_TASK_FORM_PAGE } from "src/commonJson/common";
import CustomFields from "src/components/custom-fields/CustomFields";
import { useAuthContext } from "src/auth/hooks";
import {
  updateDataTrainingTask,
  createDataTrainingTask,
} from "src/redux/slices/dataTraining";
import { ROLES } from "src/utils/constants/constants";
import CVATTooltip from "./new/CVATTooltip";
import { FIELD_TYPES } from "src/commonJson/types";
import { Upload } from "src/components/upload";
import AWS from "../../s3/aws.config";
import { AWS_CONFIG } from "../../config-global";
import ColorPicker from './new/ColorPicker';
// ----------------------------------------------------------------------

export type LabelType =
  | "rectangle"
  | "polygon"
  | "polyline"
  | "points"
  | "ellipse"
  | "cuboid"
  | "skeleton"
  | "mask"
  | "tag"
  | "any";
interface SerializedLabel {
  label: string;
  color: string;
  type?: LabelType;
}
interface FormValuesProps extends Omit<any, "avatarUrl"> {
  taskName: string;
  labels: string;
}

type Props = {
  isEdit?: boolean;
  currentTask?: any;
  edit?: boolean;
};

const NEW_LABEL_COLOR = "#b3b3b3";

export default function TaskNewEditForm({
  isEdit = false,
  currentTask,
  edit,
}: Props) {
  const navigate = useNavigate();
  const { translate } = useLocales();
  const { user } = useAuthContext();
  const isDesktop = useResponsive("up", "lg");
  const { enqueueSnackbar } = useSnackbar();

  const [roles, setRoles] = useState<any>();
  const [label, setLabel] = useState<any>([]);
  // State hook for storing the selected files
  const [files, setFiles] = useState<(File | string)[]>([]);

  useEffect(() => {
    const orgCode = routePrefix(user);
    dispatch(fetchAllRoles(orgCode))
      .then((res) => {
        setRoles(
          res?.roles.filter((role: any) => role?.name !== ROLES.SUPER_ADMIN)
        );
      })
      .catch((err) => console.log(err));
  }, []);

  const TABS = [
    {
      value: "raw",
      label: translate("Raw"),
      icon: "ph:pencil-line",
      component: <RawComponent label={label} setLabel={setLabel} />,
    },
    {
      value: "construtor",
      label: translate("Construtor"),
      icon: "fluent:stack-32-regular",
      component: (
        <ConstructorComponent label={label} setLabel={setLabel} roles={roles} />
      ),
    },
  ];

  const [currentTab, setCurrentTab] = useState(TABS[1].value);

  const NewTaskSchema = Yup.object().shape({
    taskName: Yup.string().required(translate("Task Name is required")),
    labels: Yup.lazy((value) => {
      if (value !== undefined) {
        return Yup.array()
          .min(1, translate("At Least one label is required"))
          .required(translate("At Least one label is required"));
      }
      return Yup.array();
    }),
  });

  const defaultValues = useMemo(
    () => ({
      taskName: currentTask?.taskName || "",
      lables: currentTask?.lables || "",
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentTask]
  );

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(NewTaskSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    setValue,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const values = watch();

  useEffect(() => {
    if (values.isApproved === true) setValue("isActive", true);
  }, [values.isApproved]);

  useEffect(() => {
    if (isEdit && currentTask) reset(defaultValues);

    if (!isEdit) reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, currentTask]);

  // Handle file drop
  const handleDrop = useCallback(
    /**
     * Handle the drop of accepted files.
     * @param {File[]} acceptedFiles - The files dropped into the drop zone.
     */
    (acceptedFiles: File[]) => {
      const allowedExtensions = ['.jpeg', '.jpg', '.png'];
  
      const unsupportedFiles = acceptedFiles.filter((file) => {
        const fileExtension = file.name.slice(
          ((file.name.lastIndexOf('.') - 1) >>> 0) + 2
        );
        const lowerCaseExtension = fileExtension.toLowerCase();
        if (!allowedExtensions.includes('.' + lowerCaseExtension)) {
          // Display an error message for unsupported files in the snackbar
          enqueueSnackbar('Only JPEG and PNG files are allowed.', {
            variant: 'error',
          });
          return false; // Filter out unsupported files
        }
        return true;
      });
  
      const newFiles = unsupportedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      );
  
      setFiles([...files, ...newFiles]);
      setValue('image', newFiles);
    },
    [files, setValue, enqueueSnackbar]
  );
  
  
  // Handle file removal
  const handleRemoveFile = (removedFile: File | String) => {
    // Remove the removedFile from the files state
    const updatedFiles = files.filter((file) => file !== removedFile);
    setFiles(updatedFiles);
    // Reset the 'image' field in the form
    setValue("image", updatedFiles.length > 0 ? updatedFiles : null);
  };

  /**
 * Handles the submission of a form containing image and label data. 
 * Uploads the image and label data to an AWS S3 bucket and displays success messages.
 *
 * @async
 * @function
 * @param {FormValuesProps} data - The form data, including an image and task information.
 * @throws {Error} If the S3 bucket is not defined.
 */
  const onSubmit = async (data: FormValuesProps) => {
    const orgCode = routePrefix(user);
    //calculate count of labels
    const nc = label.length;
    try {
      // Define the folder path for the task data in the S3 bucket
      const taskFolder = `${user?.organization?.orgCode}/${user?.id}/${data.taskName}`;
      const s3 = new AWS.S3();
      const uploadPromises = [];
      //loop through the selected files for upload
      for (const file of files) {
        if (file instanceof File) {
          const imageFileName = `${taskFolder}/${file.name}`;
          if (!AWS_CONFIG.s3Bucket) {
            throw new Error("S3 Bucket not defined");
          }
          const imageParams = {
            Bucket: AWS_CONFIG.s3Bucket,
            Key: imageFileName,
            Body: file,
            ContentType: file.type,
            ACL: "public-read",
            ContentDisposition: "inline",
          };
         
          const uploadPromise = new Promise((resolve, reject) => {
            //upload the file to S3
            s3.upload(imageParams, async (err: any, data: any) => {
              if (err) {
                console.error(err);
                reject(err);
              } else {
                resolve(data);
                // Create a corresponding JSON file for the image data
                const jsonFileName = file.name.replace(/\.[^/.]+$/, ".json");
                const jsonContent = JSON.stringify({}, null, 2);
                if (!AWS_CONFIG.s3Bucket) {
                  throw new Error("S3 Bucket not defined");
                }
                //Define S3 parameters for the JSON file
                const jsonFileParams = {
                  Bucket: AWS_CONFIG.s3Bucket,
                  Key: `${taskFolder}/${jsonFileName}`,
                  Body: jsonContent,
                  ContentType: "application/json",
                  ACL: "public-read",
                  ContentDisposition: "inline",
                  //Expires: new Date(Date.now() + 10000 * 1000),
                };
                //Upload the JSON file to S3
                s3.upload(jsonFileParams, async (err: any, jsonData: any) => {
                  if (err) {
                    console.error(err);
                    reject(err);
                  } else {
                    resolve(jsonData);
                  }
                });
              }
            });
          });
          //Add the upload promise to the array of promises
          uploadPromises.push(uploadPromise);
        }
      }
      // Wait for all image and JSON file uploads to complete
      await Promise.all(uploadPromises);
      // Create a JSON object with label data
      const jsonData = {
        nc: nc,
        classes: label,
      };
      // Define the file name for the label data
      const labelFileName = `${taskFolder}/${data.taskName}.json`;

      if (!AWS_CONFIG.s3Bucket) {
        throw new Error("S3 Bucket not defined");
      }
      // Define S3 parameters for the label data
      const labelDataParams = {
        Bucket: AWS_CONFIG.s3Bucket,
        Key: labelFileName,
        Body: JSON.stringify(jsonData, null, 2),
        ContentType: "application/json",
        ACL: "public-read",
        ContentDisposition: "inline",
      };
      await new Promise((resolve, reject) => {
        // Upload the label data to S3
        s3.upload(labelDataParams, async (err: any, data: any) => {
          if (err) {
            console.error(err);
            reject(err);
          } else {
            resolve(data);
          }
        });
      });
      // if (isEdit) {
      //   return dispatch(
      //     updateDataTrainingTask({ ...data, taskId: currentTask.id }, orgCode)
      //   )
      //     .then((res) => {
      //       enqueueSnackbar(translate("Update Success!"));
      //       navigate(PATH_DASHBOARD.dataTraining.list(routePrefix(user)));
      //     })
      //     .catch((err) => {
      //       console.log(err);
      //       setError("afterSubmit", {
      //         ...err,
      //         message: err.message || err,
      //       });
      //     });
      // } else {
      //   return dispatch(createDataTrainingTask(data, orgCode))
      //     .then((res) => {
      //       enqueueSnackbar(translate("Create Success!"));
      //       navigate(PATH_DASHBOARD.dataTraining.list(routePrefix(user)));
      //     })
      //     .catch((err) => {
      //       console.log(err);
      //       setError("afterSubmit", {
      //         ...err,
      //         message: err.message || err,
      //       });
      //     });
      // }
      //display success message and navigate to the task list page
      enqueueSnackbar(translate(isEdit ? 'Update Success!' : 'Create Success!'));
      // Clear the form and navigate back to the listing page
      reset();
      navigate(PATH_DASHBOARD.dataTraining.list(routePrefix(user)));
    } catch (error) {
      console.error(error);
      setError("afterSubmit", {
        ...error,
        message: error.message || error,
      });
    }
  };

  const handleReset = () => {
    //logic here
    console.log("Additional action triggered");
  };

  const renderTabs = (
    <Tabs
      value={currentTab}
      onChange={(event, newValue) => setCurrentTab(newValue)}
    >
      {TABS.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="start"
          value={tab.value}
          label={tab.label}
          sx={{ "&:not(:last-of-type)": { mr: 3 } }}
          icon={
            <Label
              variant={
                ((tab.value === TABS[1].value || tab.value === currentTab) &&
                  "filled") ||
                "soft"
              }
              color={
                (tab.value === TABS[1].value && "info") ||
                (tab.value === TABS[0].value && "success") ||
                "default"
              }
            >
              <Iconify icon={tab.icon} />
            </Label>
          }
        />
      ))}
    </Tabs>
  );

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors?.afterSubmit?.message && (
        <Alert severity="error">
          {typeof errors?.afterSubmit?.message == "string" &&
            errors?.afterSubmit?.message}
        </Alert>
      )}
      <Grid container spacing={3} sx={{ mt: 1, mb: 5 }}>
        <Grid item xs={12} md={12}>
          <Card sx={{ p: 3 }}>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: "repeat(1, 1fr)",
                sm: "repeat(1, 1fr)",
              }}
            >
              {
                /* {(!edit && !isEdit) ? DATA_TRAINING_TASK_FORM_PAGE.map((field) => { return (<CustomFields row={field} roles={roles} />);}) : ORGANIZATION_EDIT_FORM_PAGE.map((field) => { return (<CustomFields row={field} roles={roles} setValue={setValue} setError={setError} defaultValues={defaultValues} />);}) */
                !isEdit && edit ? (
                  DATA_TRAINING_TASK_FORM_PAGE.map((field) => {
                    return (
                      <CustomFields
                        row={field}
                        roles={roles}
                        setValue={setValue}
                        setError={setError}
                      />
                    );
                  })
                ) : (
                  <></>
                )
              }
              {renderTabs}
              {TABS.map(
                (tab) =>
                  tab.value === currentTab && (
                    <Box key={tab.value}>{tab.component}</Box>
                  )
              )}
              {/* {TabComponent(TABS, currentTab)} */}
              <Divider />
            </Box>

            <Upload
              multiple
              files={files}
              onDrop={handleDrop}
              onRemove={handleRemoveFile}
            />

            {edit === true && isDesktop && (
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="flex-end"
                sx={{ mt: 3 }}
              >
                <Button type="button" onClick={() => navigate(-1)}>
                  {translate("Cancel")}
                </Button>
                <LoadingButton
                  disabled={
                    errors.afterSubmit?.message === "This Name already Exists"
                      ? true
                      : false
                  }
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                >
                  {!isEdit ? translate("Create") : translate("Save Changes")}
                </LoadingButton>
              </Stack>
            )}
          </Card>
          {edit === true && !isDesktop && (
            <Paper
              sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 1,
                overflow: "hidden",
              }}
              elevation={3}
            >
              <Stack direction="row">
                <Grid container>
                  <Grid
                    item
                    xs={6}
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <Button
                      type="button"
                      variant="text"
                      color="inherit"
                      size="large"
                      sx={{ borderRadius: "0px", width: "100%" }}
                      onClick={() => navigate(-1)}
                    >
                      {" "}
                      {translate("Cancel")}
                    </Button>
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <LoadingButton
                      disabled={
                        errors.afterSubmit?.message ===
                          "This Name already Exists"
                          ? true
                          : false
                      }
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{ borderRadius: "0%", width: "100%" }}
                    >
                      {translate("Save")}
                    </LoadingButton>
                  </Grid>
                </Grid>
              </Stack>
            </Paper>
          )}
        </Grid>
      </Grid>
    </FormProvider>
  );
}

type RawPropsTypes = {
  label?: any;
  setLabel?: any;
  roles?: any;
};
const RawComponent = ({ label, setLabel }: RawPropsTypes): JSX.Element => {
  return (
    <RHFTextField
      name="jsonTextArea"
      value={JSON.stringify(label, null, 2)}
      multiline
      rows={10}
      fullWidth
      variant="outlined"
      sx={{ mt: 3 }}
      inputProps={{ readOnly: true }}
    />
  );
};

type ConstructorPropsTypes = {
  editLabelData?: any;
  isEditConstrutor?: any;
  label?: any;
  setLabel?: any;
  roles?: any;
};
let defaultLabelData: SerializedLabel={ label: "", color: NEW_LABEL_COLOR, type: "rectangle" }
const ConstructorComponent = ({
  label,
  setLabel,
  roles,
  editLabelData = defaultLabelData,
  isEditConstrutor = false,
}: ConstructorPropsTypes): JSX.Element => {
  interface ConstrutorFormValuesProps {
    label: string;
  }

  const [selectedColor, setSelectedColor] = useState<string>(""); // State to store the selected color
  const [isEdit, setIsEdit] = useState<boolean>(isEditConstrutor);
  const [editedData, setEditedData] = useState<SerializedLabel |null>(editLabelData);
  const { translate } = useLocales();

  const ConstrutorNewTaskSchema = Yup.object().shape({
    label: Yup.string().required(translate("Label is required")),
  });

  const defaultValuesConstrutor = useMemo(
    () => ({
      label: editedData?.label || "",
      color: editedData?.color || NEW_LABEL_COLOR,
      type: "rectangle",
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editedData]
  );

  const methodsConstrutor = useForm<ConstrutorFormValuesProps>({
    resolver: yupResolver(ConstrutorNewTaskSchema),
    defaultValues: defaultValuesConstrutor,
  });

  const {
    reset,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = methodsConstrutor;

  const onSubmitConstrutor = async (e: React.FormEvent) => {
    try {
      let data = watch();
      if (data?.label) {
        if (
          !isEditConstrutor &&
          label.find(
            (ele: any) =>
              (ele?.label || "").toLowerCase() === (data?.label).toLowerCase()
          )
        ) {
          handleResetConstrutor();
          setError("label", { message: "Label Cannot be same!!" });
        } else {
          // if (isEditConstrutor) {
          //     return true;
          // } else {
          setLabel([
            ...label,
            {
              label: data?.label,
              color: selectedColor ? selectedColor : NEW_LABEL_COLOR,
              type: "rectangle",
            },
          ]);
          // }
          handleResetConstrutor();
        }
      } else {
        handleResetConstrutor();
        setError("label", { message: "Label Cannot be empty!!" });
      }

    } catch (error) {
      console.error(error);
    }
  };

  const handleResetConstrutor = () => {
    reset();
    setSelectedColor(NEW_LABEL_COLOR);
    setIsEdit(false);
    setEditedData(defaultLabelData);
  };


  const handleDelete = (labelToDelete: SerializedLabel): void => {
    const deletedlabel= label?.filter((item: SerializedLabel) => {
      return item.label !== labelToDelete.label;
    });
    setLabel([...deletedlabel]);
    setIsEdit(false);
    setValue('label', "")
    setSelectedColor(NEW_LABEL_COLOR);
  };

  const handleEdit = (label: SerializedLabel): void => {
    setIsEdit(true);
    setEditedData(label);
    setValue('label', label.label)
    setSelectedColor(label.color);
  };

  const saveData = (data: SerializedLabel): void => {
    const updatedlabel= label?.map((item: SerializedLabel) => {
      if (item.label === editedData?.label) {
        item.label = data.label;
        item.color = data.color;
      }
      return item;
    });
    setLabel([...updatedlabel]);
    setIsEdit(false);
    setEditedData(defaultLabelData);
    setValue('label', "")
    setSelectedColor(NEW_LABEL_COLOR);
  }
  
useEffect(() => {
  if (isEdit) {
    setValue('label', editedData?.label || "")
    setSelectedColor(editedData?.color || NEW_LABEL_COLOR);
  }
}, [isEdit,editedData])

  return (
    <>
      <FormProvider methods={methodsConstrutor}>
        <Grid container spacing={3} sx={{ mt: 1, mb: 5 }}>
          <Grid item xs={12} md={12}>
            <Card sx={{ p: 3 }}>
              <Box
                rowGap={3}
                columnGap={2}
                display="grid"
                gridTemplateColumns={{
                  xs: "repeat(1, 1fr)",
                  sm: "repeat(3, 1fr)",
                }}
              >

                <CustomFields
                  row={{
                    id: "construtorKey",
                    name: "label",
                    label: "Label Name",
                    type: FIELD_TYPES.TEXT,
                    required: true,
                    readOnly: false,
                  }}
                  defaultValues={defaultValuesConstrutor}
                  roles={roles}
                  setValue={setValue}
                  setError={setError}
                />
                {/* {renderChangeColorButton()} */}
                <ColorPicker
                  selectedColor={selectedColor}
                  setSelectedColor={setSelectedColor}
                  defaultColor={selectedColor || NEW_LABEL_COLOR}
                />
                {/* {edit === true && isDesktop && */}
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="flex-end"
                  sx={{ mt: 3 }}
                >              
                  <Button type="button" onClick={() => handleResetConstrutor()}>
                    {translate("Reset")}
                  </Button>
                  {!isEdit?(
                  <Button
                    type="button"
                    onClick={(e) => onSubmitConstrutor(e)}
                    variant="contained"
                  >
                    {translate("Add")}
                  </Button>
                  ):
                  (
                    <Button
                    type="button"
                    onClick={(e) => saveData({label:watch().label,color:selectedColor})}
                    variant="contained"
                    >
                      {translate("Update")}
                    </Button>
                  )
}

                </Stack>
                {/* } */}
              </Box>

              {/* <Box rowGap={3} columnGap={2} display="grid" gridTemplateColumns={{ xs: 'repeat(1, 1fr)', sm: 'repeat(4, 1fr)', }} sx={{mt:"8px"}} > */}
              {label.map((chipData: any,index:any) => {
                return (
                  <>
                    <ConstructorViewerItem
                      key={index}
                      chipData={chipData}
                      onUpdate={handleEdit}
                      onDelete={handleDelete}
                    />
                  </>
                );
              })}

              {/* </Box> */}
            </Card>
          </Grid>
        </Grid>
      </FormProvider>
    </>
  );
};

type ConstructorViewerItemProp = {
  chipData: any;
  onUpdate: (labeltoEdit: SerializedLabel) => void;
  onDelete: (labelToDelete: SerializedLabel) => void;
};

const ChipLabel = ({
  color,
  textColor,
  label,
  onUpdate,
}: {
  color: string;
  textColor: string;
  label: any;
  onUpdate: (label: SerializedLabel) => void;
}): JSX.Element => {
  return (
    <Stack
      direction="row"
      spacing={2}
      justifyContent="start"
      alignItems="center"
    >
      <Typography
        variant="subtitle2"
        noWrap
        sx={{ color: textColor, backgroundColor: "inherit" }}
      >
        {label}
      </Typography>
      <CVATTooltip title="Update attributes">   
        <IconButton
          sx={{ color: textColor }}
          onClick={(): void => onUpdate({label, color})}
        >
          <Iconify icon="eva:edit-fill" />
        </IconButton>
      </CVATTooltip>
    </Stack>
  );
};

const ConstructorViewerItem = ({
  chipData,
  onUpdate,
  onDelete,
}: ConstructorViewerItemProp): JSX.Element => {
  const { color, label, type } = chipData;
  const backgroundColor = color || NEW_LABEL_COLOR;
  let textColor = "#ffffff";
  try {
    // convert color to grayscale and from the result get better text color
    // (for darken background -> lighter text, etc.)
    const [r, g, b] = [
      backgroundColor.slice(1, 3),
      backgroundColor.slice(3, 5),
      backgroundColor.slice(5, 7),
    ];
    const grayscale = (parseInt(r, 16) + parseInt(g, 16) + parseInt(b, 16)) / 3;
    if (grayscale - 128 >= 0) {
      textColor = "#000000";
    }
  } catch (_: any) {
    // nothing to do
  }
  return (
    <>
      <Chip
        label={
          <ChipLabel textColor={textColor} label={label} onUpdate={onUpdate} color={color} />
        }
        variant="soft"
        sx={{ backgroundColor, ml: "7px" }}
        onDelete={(): void => onDelete({label,color,type})}
        deleteIcon={
          <>
            <CVATTooltip title="Delete label">
              <IconButton
                sx={{ color: textColor }}
                onClick={(): void => onDelete({label,color,type})}
              >
                <Iconify icon="eva:trash-2-outline" />
              </IconButton>
            </CVATTooltip>
          </>
        }
      />
      {/* <Chip sx={{ backgroundColor: backgroundColor, border: 0 }}>
                <Stack direction='row' spacing={2} justifyContent='start' alignItems='center'>
                <Typography variant='subtitle2' noWrap sx={{ color: textColor, backgroundColor: 'inherit' }}></Typography>
                <CVATTooltip title='Update attributes'><IconButton sx={{color:textColor}} onClick={(): void => onUpdate(label)}><Iconify icon="eva:edit-fill" /></IconButton></CVATTooltip>
                <CVATTooltip title='Delete label'><IconButton sx={{color:textColor}} onClick={(): void => onDelete(label)}><Iconify icon="eva:trash-2-outline" /></IconButton></CVATTooltip>
                </Stack>
            </Chip> */}
    </>
  );
};
